/**
 * Диспозиция по ситуации — три жеста и ни одного четвёртого.
 *
 * «Заметили» фиксирует, что человек это видит, «отреагировали» — что он
 * сделал, «закрыли» — что здесь закончено. Ни правки, ни отката: след решения
 * не переписывается, исправление выглядит следующим жестом со своими словами.
 *
 * Основания клиент НЕ присылает — их считает сервер тем же детектором, что
 * показал строку. Право на запись тоже проверяет сервер; `can_manage_situations`
 * из `/auth/me` здесь решает только, рисовать ли кнопку: спрятанная кнопка
 * ничего не защищает и защитой не считается.
 */

import { useState } from 'react';
import { api, ApiError, type QueryParams } from '../../../api/client';
import { useAuth } from '../../../auth/context';
import type { Situation } from '../../astra/situations';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';

const m = defineMessages({
  noticed: {
    id: 'data.disposition.noticed',
    defaultMessage: 'Заметили',
    description: 'Кнопка и статус отметки по ситуации (очередь внимания): человек увидел строку и берёт в работу; коротко',
  },
  responded: {
    id: 'data.disposition.responded',
    defaultMessage: 'Отреагировали',
    description: 'Кнопка и статус отметки по ситуации: человек сделал что-то по строке; коротко',
  },
  closed: {
    id: 'data.disposition.closed',
    defaultMessage: 'Закрыли',
    description: 'Кнопка и статус отметки по ситуации: здесь всё закончено; коротко',
  },
  noticedHint: {
    id: 'data.disposition.noticedHint',
    defaultMessage: 'Строка увиденная: человек её видел и берёт в работу.',
    description: 'Подсказка под кнопками отметки по ситуации, когда выбрано «Заметили»',
  },
  respondedHint: {
    id: 'data.disposition.respondedHint',
    defaultMessage: 'Что именно сделано — своими словами, это уедет в след решения.',
    description: 'Подсказка под кнопками отметки по ситуации, когда выбрано «Отреагировали»',
  },
  closedHint: {
    id: 'data.disposition.closedHint',
    defaultMessage: 'Здесь закончено. Отката нет: исправление — следующий жест.',
    description: 'Подсказка под кнопками отметки по ситуации, когда выбрано «Закрыли»',
  },
  stateWithComment: {
    id: 'data.disposition.stateWithComment',
    defaultMessage: 'Состояние: {status}. {comment}',
    description: 'Текст для роли без права записи: текущее состояние и комментарий; {status} — «Заметили», {comment} — слова человека',
  },
  stateReadOnly: {
    id: 'data.disposition.readOnly',
    defaultMessage: 'Диспозиция по ситуации записывается ролью, у которой есть это право. Чтение открыто.',
    description: 'Текст для роли без права отмечать ситуации, когда отметок ещё нет',
  },
  state: {
    id: 'data.disposition.state',
    defaultMessage: 'Состояние: {status}',
    description: 'Строка над кнопками отметки по ситуации: текущее состояние; {status} — «Заметили»',
  },
  stateComment: {
    id: 'data.disposition.stateComment',
    defaultMessage: 'Состояние: {status} · {comment}',
    description: 'Строка над кнопками отметки по ситуации: состояние и комментарий; {status} — «Заметили», {comment} — слова человека',
  },
  commentLabel: {
    id: 'data.disposition.commentLabel',
    defaultMessage: 'Что видно и что сделано',
    description: 'aria-label поля комментария к отметке по ситуации',
  },
  commentPlaceholder: {
    id: 'data.disposition.commentPlaceholder',
    defaultMessage: 'Своими словами: что видно и что сделано',
    description: 'Подсказка в поле комментария к отметке по ситуации',
  },
  saving: {
    id: 'data.disposition.saving',
    defaultMessage: 'Записываем…',
    description: 'Кнопка отправки отметки в процессе; коротко',
  },
  save: {
    id: 'data.disposition.save',
    defaultMessage: 'Записать',
    description: 'Кнопка отправки отметки по ситуации; коротко',
  },
  failed: {
    id: 'data.disposition.failed',
    defaultMessage: 'Запись не прошла: {reason}',
    description: 'Ошибка записи отметки по ситуации; {reason} — ответ сервера',
  },
});


const STEPS = [
  { id: 'noticed' as const, label: m.noticed, hint: m.noticedHint },
  { id: 'responded' as const, label: m.responded, hint: m.respondedHint },
  { id: 'closed' as const, label: m.closed, hint: m.closedHint },
];

const STATUS_LABEL = { noticed: m.noticed, responded: m.responded, closed: m.closed } as const;

export function SituationDisposition({
  situation,
  params,
  onDone,
}: {
  situation: Situation;
  params: QueryParams;
  onDone: () => void;
}) {
  const t = useT();
  const { principal } = useAuth();
  const statusText = (status: string): string => {
    const label = STATUS_LABEL[status as keyof typeof STATUS_LABEL];
    return label ? t(label) : status;
  };
  const [step, setStep] = useState<'noticed' | 'responded' | 'closed' | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = situation.key;
  const current = situation.disposition ?? null;

  if (!key) return null;
  if (!principal?.can_manage_situations) {
    return (
      <div className="astra-situation-action">
        <span>
          {current
            ? t(m.stateWithComment, { status: statusText(current.status), comment: current.comment })
            : t(m.stateReadOnly)}
        </span>
      </div>
    );
  }

  const send = async () => {
    if (!step) return;
    setBusy(true);
    setError(null);
    try {
      await api.csrf();
      const call =
        step === 'noticed' ? api.noticeSituation : step === 'responded' ? api.respondSituation : api.closeSituation;
      await call(key, comment.trim(), params);
      setStep(null);
      setComment('');
      onDone();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="astra-situation-action" data-guide="action">
      {current && (
        <span>
          {current.comment
            ? t(m.stateComment, { status: statusText(current.status), comment: current.comment })
            : t(m.state, { status: statusText(current.status) })}
        </span>
      )}
      <div className="astra-situation-links">
        {STEPS.map((row) => (
          <button
            key={row.id}
            type="button"
            className={`astra-button ${step === row.id ? 'astra-button-primary' : ''}`}
            aria-pressed={step === row.id}
            onClick={() => setStep(step === row.id ? null : row.id)}
          >
            {t(row.label)}
          </button>
        ))}
      </div>
      {step && (
        <>
          <p className="astra-footnote">{t(STEPS.find((row) => row.id === step)!.hint)}</p>
          <label className="astra-situation-search">
            <input
              aria-label={t(m.commentLabel)}
              placeholder={t(m.commentPlaceholder)}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          </label>
          <button className="astra-button astra-button-primary" disabled={busy} onClick={() => void send()}>
            {busy ? t(m.saving) : t(m.save)}
          </button>
        </>
      )}
      {error && <p className="astra-footnote">{t(m.failed, { reason: error })}</p>}
    </div>
  );
}
